import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Button, Card, CardContent, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, Grid, IconButton, InputLabel, MenuItem, Paper, Select,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography, Tabs, Tab, Chip, Link
} from '@mui/material';
import { Add, Delete, Edit, AttachFile, Image as ImageIcon } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { getApartments, getApartmentUsers, getCorpusFundSummary, getCorpusContributions, createCorpusContribution, updateCorpusContribution, deleteCorpusContribution, getCorpusExpenses, createCorpusExpense, updateCorpusExpense, deleteCorpusExpense, USE_MOCK_DATA } from '../services/api';
import { mockApartments, mockApartmentUsers, mockCorpusFundSummary, mockCorpusContributions, mockCorpusExpenses } from '../services/mockData';

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

export default function CorpusFund() {
  const [apartments, setApartments] = useState([]);
  const [selectedApartment, setSelectedApartment] = useState('');
  const [apartmentUsers, setApartmentUsers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [contributions, setContributions] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [tab, setTab] = useState(0);

  // Contribution dialog
  const [contribOpen, setContribOpen] = useState(false);
  const [editingContrib, setEditingContrib] = useState(null);
  const [contribForm, setContribForm] = useState({ apartmentUserId: '', flatNumber: '', amount: '', contributionDate: '', notes: '' });

  // Expense dialog
  const [expenseOpen, setExpenseOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [expenseForm, setExpenseForm] = useState({ description: '', amount: '', expenseDate: '', notes: '' });
  const [billImage, setBillImage] = useState(null);
  const fileInputRef = useRef(null);

  const { isAdmin } = useAuth();

  useEffect(() => {
    if (USE_MOCK_DATA) {
      setApartments([mockApartments[0]]);
      setSelectedApartment(mockApartments[0].id);
      return;
    }
    getApartments().then(res => {
      setApartments(res.data);
      if (res.data.length > 0) setSelectedApartment(res.data[0].id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedApartment) return;
    if (USE_MOCK_DATA) {
      setSummary(mockCorpusFundSummary);
      setContributions(mockCorpusContributions);
      setExpenses(mockCorpusExpenses);
      setApartmentUsers(mockApartmentUsers);
      return;
    }
    getCorpusFundSummary(selectedApartment).then(res => setSummary(res.data)).catch(() => {});
    getCorpusContributions(selectedApartment).then(res => setContributions(res.data)).catch(() => {});
    getCorpusExpenses(selectedApartment).then(res => setExpenses(res.data)).catch(() => {});
    getApartmentUsers(selectedApartment).then(res => setApartmentUsers(res.data)).catch(() => {});
  }, [selectedApartment]);

  // ===== CONTRIBUTION HANDLERS =====
  const handleAddContribution = async () => {
    if (editingContrib) {
      if (USE_MOCK_DATA) {
        setContributions(prev => prev.map(c => c.id === editingContrib.id ? { ...c, amount: parseFloat(contribForm.amount), contributionDate: contribForm.contributionDate, notes: contribForm.notes } : c));
      } else {
        await updateCorpusContribution(editingContrib.id, { amount: parseFloat(contribForm.amount), contributionDate: contribForm.contributionDate, notes: contribForm.notes || null });
        getCorpusContributions(selectedApartment).then(res => setContributions(res.data));
        getCorpusFundSummary(selectedApartment).then(res => setSummary(res.data));
      }
    } else {
      if (USE_MOCK_DATA) {
        const au = apartmentUsers.find(u => u.id === parseInt(contribForm.apartmentUserId));
        setContributions(prev => [...prev, { id: Date.now(), apartmentId: selectedApartment, apartmentUserId: parseInt(contribForm.apartmentUserId), flatNumber: contribForm.flatNumber, userName: au?.userName || '', amount: parseFloat(contribForm.amount), contributionDate: contribForm.contributionDate, notes: contribForm.notes || null, createdAt: new Date().toISOString() }]);
        setSummary(prev => ({ ...prev, totalContributions: prev.totalContributions + parseFloat(contribForm.amount), balance: prev.balance + parseFloat(contribForm.amount) }));
      } else {
        await createCorpusContribution({ apartmentId: selectedApartment, apartmentUserId: parseInt(contribForm.apartmentUserId), flatNumber: contribForm.flatNumber, amount: parseFloat(contribForm.amount), contributionDate: contribForm.contributionDate, notes: contribForm.notes || null });
        getCorpusContributions(selectedApartment).then(res => setContributions(res.data));
        getCorpusFundSummary(selectedApartment).then(res => setSummary(res.data));
      }
    }
    closeContribDialog();
  };

  const handleEditContribution = (c) => {
    setEditingContrib(c);
    setContribForm({ apartmentUserId: c.apartmentUserId, flatNumber: c.flatNumber, amount: c.amount.toString(), contributionDate: c.contributionDate.split('T')[0], notes: c.notes || '' });
    setContribOpen(true);
  };

  const closeContribDialog = () => {
    setContribOpen(false);
    setEditingContrib(null);
    setContribForm({ apartmentUserId: '', flatNumber: '', amount: '', contributionDate: '', notes: '' });
  };

  // ===== EXPENSE HANDLERS =====
  const handleAddExpense = async () => {
    if (editingExpense) {
      if (USE_MOCK_DATA) {
        setExpenses(prev => prev.map(e => e.id === editingExpense.id ? { ...e, description: expenseForm.description, amount: parseFloat(expenseForm.amount), expenseDate: expenseForm.expenseDate, notes: expenseForm.notes } : e));
      } else {
        await updateCorpusExpense(editingExpense.id, { description: expenseForm.description, amount: parseFloat(expenseForm.amount), expenseDate: expenseForm.expenseDate, notes: expenseForm.notes || null });
        getCorpusExpenses(selectedApartment).then(res => setExpenses(res.data));
        getCorpusFundSummary(selectedApartment).then(res => setSummary(res.data));
      }
    } else {
      if (USE_MOCK_DATA) {
        setExpenses(prev => [...prev, { id: Date.now(), apartmentId: selectedApartment, description: expenseForm.description, amount: parseFloat(expenseForm.amount), expenseDate: expenseForm.expenseDate, billImageUrl: billImage ? URL.createObjectURL(billImage) : null, notes: expenseForm.notes || null, createdByUser: 'Debug Admin', createdAt: new Date().toISOString() }]);
        setSummary(prev => ({ ...prev, totalExpenses: prev.totalExpenses + parseFloat(expenseForm.amount), balance: prev.balance - parseFloat(expenseForm.amount) }));
      } else {
        await createCorpusExpense({ apartmentId: selectedApartment, description: expenseForm.description, amount: parseFloat(expenseForm.amount), expenseDate: expenseForm.expenseDate, notes: expenseForm.notes || null, billImage });
        getCorpusExpenses(selectedApartment).then(res => setExpenses(res.data));
        getCorpusFundSummary(selectedApartment).then(res => setSummary(res.data));
      }
    }
    closeExpenseDialog();
  };

  const handleEditExpense = (e) => {
    setEditingExpense(e);
    setExpenseForm({ description: e.description, amount: e.amount.toString(), expenseDate: e.expenseDate.split('T')[0], notes: e.notes || '' });
    setBillImage(null);
    setExpenseOpen(true);
  };

  const closeExpenseDialog = () => {
    setExpenseOpen(false);
    setEditingExpense(null);
    setExpenseForm({ description: '', amount: '', expenseDate: '', notes: '' });
    setBillImage(null);
  };

  const handleDeleteContribution = async (id) => {
    if (window.confirm('Delete this contribution?')) {
      if (USE_MOCK_DATA) setContributions(prev => prev.filter(c => c.id !== id));
      else { await deleteCorpusContribution(id); getCorpusContributions(selectedApartment).then(res => setContributions(res.data)); getCorpusFundSummary(selectedApartment).then(res => setSummary(res.data)); }
    }
  };

  const handleDeleteExpense = async (id) => {
    if (window.confirm('Delete this expense?')) {
      if (USE_MOCK_DATA) setExpenses(prev => prev.filter(e => e.id !== id));
      else { await deleteCorpusExpense(id); getCorpusExpenses(selectedApartment).then(res => setExpenses(res.data)); getCorpusFundSummary(selectedApartment).then(res => setSummary(res.data)); }
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>Corpus Fund</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Reserve fund for major expenses. Visible to flat owners only.
      </Typography>

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <Card sx={{ bgcolor: '#f0fff4' }}><CardContent>
              <Typography color="text.secondary" gutterBottom>Total Contributions</Typography>
              <Typography variant="h5" color="success.main">₹{summary.totalContributions.toLocaleString('en-IN')}</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card sx={{ bgcolor: '#fff5f5' }}><CardContent>
              <Typography color="text.secondary" gutterBottom>Total Expenses</Typography>
              <Typography variant="h5" color="error">₹{summary.totalExpenses.toLocaleString('en-IN')}</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card sx={{ bgcolor: summary.balance >= 0 ? '#e3f2fd' : '#fff5f5' }}><CardContent>
              <Typography color="text.secondary" gutterBottom>Fund Balance</Typography>
              <Typography variant="h5" color={summary.balance >= 0 ? 'primary' : 'error'}>₹{summary.balance.toLocaleString('en-IN')}</Typography>
            </CardContent></Card>
          </Grid>
        </Grid>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1 }}>
        <Tab label="Contributions" />
        <Tab label="Expenses" />
      </Tabs>

      {/* Contributions Tab */}
      <TabPanel value={tab} index={0}>
        {isAdmin() && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button variant="contained" startIcon={<Add />} onClick={() => { setEditingContrib(null); setContribOpen(true); }}>Add Contribution</Button>
          </Box>
        )}
        <TableContainer component={Paper}>
          <Table>
            <TableHead><TableRow>
              <TableCell>Date</TableCell><TableCell>Flat</TableCell><TableCell>Name</TableCell>
              <TableCell align="right">Amount</TableCell><TableCell>Notes</TableCell>
              {isAdmin() && <TableCell>Actions</TableCell>}
            </TableRow></TableHead>
            <TableBody>
              {contributions.map(c => (
                <TableRow key={c.id}>
                  <TableCell>{new Date(c.contributionDate).toLocaleDateString()}</TableCell>
                  <TableCell>{c.flatNumber}</TableCell>
                  <TableCell>{c.userName}</TableCell>
                  <TableCell align="right">₹{c.amount.toLocaleString('en-IN')}</TableCell>
                  <TableCell>{c.notes || '—'}</TableCell>
                  {isAdmin() && (
                    <TableCell>
                      <IconButton onClick={() => handleEditContribution(c)} color="primary"><Edit /></IconButton>
                      <IconButton onClick={() => handleDeleteContribution(c.id)} color="error"><Delete /></IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {contributions.length > 0 && (
                <TableRow>
                  <TableCell colSpan={3}><strong>Total</strong></TableCell>
                  <TableCell align="right"><strong>₹{contributions.reduce((s, c) => s + c.amount, 0).toLocaleString('en-IN')}</strong></TableCell>
                  <TableCell colSpan={2} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Expenses Tab */}
      <TabPanel value={tab} index={1}>
        {isAdmin() && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button variant="contained" startIcon={<Add />} onClick={() => { setEditingExpense(null); setExpenseOpen(true); }}>Add Expense</Button>
          </Box>
        )}
        <TableContainer component={Paper}>
          <Table>
            <TableHead><TableRow>
              <TableCell>Date</TableCell><TableCell>Description</TableCell>
              <TableCell align="right">Amount</TableCell><TableCell>Bill</TableCell>
              <TableCell>Notes</TableCell><TableCell>Added By</TableCell>
              {isAdmin() && <TableCell>Actions</TableCell>}
            </TableRow></TableHead>
            <TableBody>
              {expenses.map(e => (
                <TableRow key={e.id}>
                  <TableCell>{new Date(e.expenseDate).toLocaleDateString()}</TableCell>
                  <TableCell>{e.description}</TableCell>
                  <TableCell align="right">₹{e.amount.toLocaleString('en-IN')}</TableCell>
                  <TableCell>
                    {e.billImageUrl ? (
                      <Link href={e.billImageUrl} target="_blank" rel="noopener">
                        <Chip icon={<ImageIcon />} label="View" size="small" clickable color="primary" variant="outlined" />
                      </Link>
                    ) : '—'}
                  </TableCell>
                  <TableCell>{e.notes || '—'}</TableCell>
                  <TableCell>{e.createdByUser}</TableCell>
                  {isAdmin() && (
                    <TableCell>
                      <IconButton onClick={() => handleEditExpense(e)} color="primary"><Edit /></IconButton>
                      <IconButton onClick={() => handleDeleteExpense(e.id)} color="error"><Delete /></IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {expenses.length > 0 && (
                <TableRow>
                  <TableCell colSpan={2}><strong>Total</strong></TableCell>
                  <TableCell align="right"><strong>₹{expenses.reduce((s, e) => s + e.amount, 0).toLocaleString('en-IN')}</strong></TableCell>
                  <TableCell colSpan={4} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Contribution Dialog */}
      <Dialog open={contribOpen} onClose={closeContribDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingContrib ? 'Edit Contribution' : 'Add Corpus Fund Contribution'}</DialogTitle>
        <DialogContent>
          {!editingContrib && (
            <FormControl fullWidth margin="normal">
              <InputLabel>Flat / Resident</InputLabel>
              <Select value={contribForm.apartmentUserId} label="Flat / Resident" onChange={(e) => {
                const selected = apartmentUsers.find(au => au.id === e.target.value);
                setContribForm({ ...contribForm, apartmentUserId: e.target.value, flatNumber: selected?.flatNumber || '' });
              }}>
                {apartmentUsers.map(au => <MenuItem key={au.id} value={au.id}>{au.flatNumber} - {au.userName}</MenuItem>)}
              </Select>
            </FormControl>
          )}
          {!editingContrib && <TextField fullWidth label="Flat Number" margin="dense" value={contribForm.flatNumber} onChange={(e) => setContribForm({ ...contribForm, flatNumber: e.target.value })} />}
          <TextField fullWidth label="Amount" type="number" margin="normal" value={contribForm.amount} onChange={(e) => setContribForm({ ...contribForm, amount: e.target.value })} />
          <TextField fullWidth label="Date" type="date" margin="normal" InputLabelProps={{ shrink: true }} value={contribForm.contributionDate} onChange={(e) => setContribForm({ ...contribForm, contributionDate: e.target.value })} />
          <TextField fullWidth label="Notes (Optional)" margin="normal" value={contribForm.notes} onChange={(e) => setContribForm({ ...contribForm, notes: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeContribDialog}>Cancel</Button>
          <Button onClick={handleAddContribution} variant="contained">{editingContrib ? 'Update' : 'Add'}</Button>
        </DialogActions>
      </Dialog>

      {/* Expense Dialog */}
      <Dialog open={expenseOpen} onClose={closeExpenseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingExpense ? 'Edit Corpus Expense' : 'Add Corpus Fund Expense'}</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Description" margin="normal" value={expenseForm.description} onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })} />
          <TextField fullWidth label="Amount" type="number" margin="normal" value={expenseForm.amount} onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })} />
          <TextField fullWidth label="Date" type="date" margin="normal" InputLabelProps={{ shrink: true }} value={expenseForm.expenseDate} onChange={(e) => setExpenseForm({ ...expenseForm, expenseDate: e.target.value })} />
          <TextField fullWidth label="Notes (Optional)" margin="normal" value={expenseForm.notes} onChange={(e) => setExpenseForm({ ...expenseForm, notes: e.target.value })} />

          {/* Bill Image Upload - only for new expenses */}
          {!editingExpense && (
            <Box sx={{ mt: 2, p: 2, border: '1px dashed #ccc', borderRadius: 1, textAlign: 'center' }}>
              <input type="file" ref={fileInputRef} onChange={(e) => { const f = e.target.files[0]; if (f && f.size <= 5*1024*1024) setBillImage(f); else if (f) alert('Max 5MB'); }} accept=".jpg,.jpeg,.png,.pdf,.webp" style={{ display: 'none' }} />
              <Button variant="outlined" startIcon={<AttachFile />} onClick={() => fileInputRef.current?.click()}>
                {billImage ? 'Change Bill Image' : 'Upload Bill Image'}
              </Button>
              {billImage && <Chip label={billImage.name} onDelete={() => setBillImage(null)} size="small" color="success" sx={{ mt: 1 }} />}
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>JPG, PNG, PDF, WebP (max 5MB)</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeExpenseDialog}>Cancel</Button>
          <Button onClick={handleAddExpense} variant="contained">{editingExpense ? 'Update' : 'Add'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
